import React from 'react';
import {
  Box,
  Flex,
  Text,
  Stat,
  StatHelpText,
  StatArrow,
  Icon,
  Card,
  CardBody,
  Spinner,
  useColorModeValue,
} from '@chakra-ui/react';

/**
 * StatCard - Reusable card component for displaying stats with icon and optional change indicator
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {number|string} props.value - Main value to display
 * @param {Function} props.icon - Icon component to display
 * @param {number} [props.change] - Percentage change (positive or negative)
 * @param {string} props.colorScheme - Color scheme for the icon
 * @param {Function} [props.formatter] - Function to format the value
 * @param {boolean} [props.isLoading] - Loading state
 * @param {string} [props.footerText] - Additional text to display at the bottom
 */
const StatCard = ({ 
  title, 
  value, 
  icon, 
  change, 
  colorScheme, 
  formatter, 
  isLoading, 
  footerText 
}) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.200`);
  const iconColor = useColorModeValue('white', 'gray.800');

  const displayValue = formatter ? formatter(value) : value;

  return (
    <Card boxShadow="md" bg={cardBg}>
      <CardBody p={4}>
        <Flex alignItems="center">
          <Box
            bg={iconBg}
            color={iconColor}
            p={3}
            borderRadius="lg"
            mr={4}
            boxShadow="md"
          >
            <Icon as={icon} boxSize={6} />
          </Box>
          <Box flex="1">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
            {isLoading ? (
              <Spinner size="sm" mt={1} colorScheme={colorScheme} />
            ) : (
              <Text fontSize="2xl" fontWeight="bold">
                {displayValue}
              </Text>
            )}
            {change !== undefined && (
              <Stat>
                <StatHelpText mb={0}>
                  <StatArrow type={change > 0 ? 'increase' : 'decrease'} />
                  {Math.abs(change)}%
                </StatHelpText>
              </Stat>
            )}
            {footerText && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {footerText}
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

export default StatCard;